import { defineStore } from 'pinia';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import {
  PayByLinkConfigurationDTO,
  ErrorResponseDTO,
  PayByLinkStatusResponseDTO,
  PayByLinkConfirmationResponseDTO,
} from 'src/services/api/data-contracts';
import {
  PayByLinkAction,
  PayByLinkTransaction,
  PayByLinkType,
  IPayByLinkSessionState,
} from 'src/types/paybylink';
import { Notify } from 'quasar';
import { AxiosError, AxiosResponse } from 'axios';
import { usePropertyStore } from 'src/stores/propertyStore';
import { SessionStorage } from 'quasar';

const apiService = Services.getService(Api);

function isErrorResponseDTO(obj: unknown): obj is ErrorResponseDTO {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    ('Message' in obj || 'Errors' in obj)
  );
}

function isPayByLinkConfirmationResponseDTO(
  obj: unknown,
): obj is PayByLinkConfirmationResponseDTO {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    ('Type' in obj ||
      'Action' in obj ||
      'Success' in obj ||
      'Message' in obj)
  );
}


export const usePayByLinkStore = defineStore('payByLinkStore', {
  state: () => ({
    propertyId: null as number | null,
    config: null as PayByLinkConfigurationDTO | null,
    transaction: null as PayByLinkTransaction | null,
    invalidLinkMessage: null as string | null,
    isProcessing: false as boolean,

    // For display and confirmation
    InvoiceNumber: null as string | null,
    Description: null as string | null,
    Type: 'None' as PayByLinkType,
    Action: 'None' as PayByLinkAction,
    PageUrl: null as string | null,
    AmountToPay: null as number,
    TransactionReference: null as string,

    isTransactionSuccessful: false as boolean,
    transactionResponseMessage: false as boolean,

    isLoadingConfirmation: false as boolean,
    confirmationToken: null as string,
  }),
  getters: {
    payByPayStack(): boolean {
      const ps = usePropertyStore();
      return ps.PayByPayStack;
    },

    getPageUrl(): string {
      return this.router.currentRoute.value.fullPath;
    },
    canSaveTransaction(): boolean {
      return (
        !this.isProcessing &&
        this.transaction != null &&
        this.transaction.CreditCard.isEmailValid &&
        (this.payByPayStack ||
          (!this.payByPayStack && this.transaction.CreditCard.isValid))
      );
    },

    isInvoice(): boolean {
      return this.Action == PayByLinkAction.Pay;
    },

    canStoreCard(): boolean {
      return this.Action == PayByLinkAction.StoreCard;
    },

    canPayInvoice(): boolean {
      return (
        this.transaction != null &&
        this.transaction.Action == PayByLinkAction.Pay &&
        this.transaction.AmountToPay > 0
      );
    },
  },
  actions: {
    deleteSession(sessionKey: string) {
      SessionStorage.remove(sessionKey);
    },
    hydrateStateFromSession(sessionKey: string) {
      let hydrationSuccess = false;
      const item = SessionStorage.getItem(sessionKey);
      const jsonData = item as IPayByLinkSessionState;
      if (jsonData) {
        hydrationSuccess = true;

        const state = jsonData;

        this.propertyId = state.PropertyId;
        this.InvoiceNumber = state.transaction.InvoiceNumber;
        this.Description = state.transaction.Description;
        this.AmountToPay = state.transaction.AmountToPay;
        this.Action = state.transaction.Action;
        this.Type = state.transaction.Type;
        this.PageUrl = state.PageUrl;

        const stateTransaction = state.transaction;

        const transaction = new PayByLinkTransaction(
          this.propertyId,
          null,
          this.config,
        );

        transaction.EntityId = stateTransaction.EntityId;
        transaction.Type = stateTransaction.Type as PayByLinkType;
        transaction.Action = stateTransaction.Action as PayByLinkAction;
        transaction.Timestamp = stateTransaction.Timestamp;
        transaction.InvoiceNumber = stateTransaction.InvoiceNumber;
        transaction.Description = stateTransaction.Description;
        transaction.AmountToPay = stateTransaction.AmountToPay;
        transaction.PaymentId = stateTransaction.PaymentId;
        transaction.Hash = stateTransaction.Hash;

        transaction.CreditCard.creditCardNumber =
          stateTransaction.CreditCard.creditCardNumber;
        transaction.CreditCard.creditCardName =
          stateTransaction.CreditCard.creditCardName;
        transaction.CreditCard.cardHolderEmail =
          stateTransaction.CreditCard.cardHolderEmail;
        transaction.CreditCard.cardType = stateTransaction.CreditCard.cardType;
        transaction.CreditCard.creditCardId =
          stateTransaction.CreditCard.creditCardId;
        transaction.CreditCard.cvvNumber =
          stateTransaction.CreditCard.cvvNumber;
        transaction.CreditCard.expiryMonth =
          stateTransaction.CreditCard.expiryMonth;
        transaction.CreditCard.expiryYear =
          stateTransaction.CreditCard.expiryYear;
        transaction.CreditCard.saveCardForFutureUse =
          stateTransaction.CreditCard.saveCardForFutureUse;

        this.transaction = transaction;
      }
      return hydrationSuccess;
    },
    persistStateToSession(sessionKey: string) {
      const value = {
        PropertyId: this.propertyId,
        transaction: this.transaction,
        PageUrl: this.PageUrl ?? this.getPageUrl,
      } as IPayByLinkSessionState;

      SessionStorage.set(sessionKey, value);
    },

    checkStatus(
      sessionKey: string,
    ): Promise<AxiosResponse<PayByLinkStatusResponseDTO, string>> {
      const propertyStore = usePropertyStore();
      const propertyId = propertyStore.propertyId;

      const statusPromise = apiService.payByLinkStatus({
        propertyId: propertyId,
        referenceNumber: sessionKey,
      });

      statusPromise.then((statusResponse) => {
        if (statusResponse.data != null) {
          if (!statusResponse.data.Success) {
            Notify.create({
              color: 'negative',
              position: 'top',
              message: statusResponse.data.Message,
            });
          }
        }
      });

      return statusPromise;
    },

    loadConfirmation(confirmationToken: string) {
      this.isLoadingConfirmation = true;
      this.confirmationToken = confirmationToken;
      const propertyStore = usePropertyStore();
      const propertyId = propertyStore.propertyId;

      const confirmationPromise = apiService.payByLinkConfirmation({
        propertyId: propertyId,
        referenceNumber: confirmationToken,
      });

      confirmationPromise.then((confirmationResponse) => {
        if (confirmationResponse.data != null) {
          this.InvoiceNumber = confirmationResponse.data.InvoiceNumber;
          this.Action = confirmationResponse.data.Action as PayByLinkAction;
          this.Type = confirmationResponse.data.Type as PayByLinkType;
          this.Description = confirmationResponse.data.Description;
          this.AmountToPay = confirmationResponse.data.AmountToPay;
          this.TransactionReference =
            confirmationResponse.data.TransactionReference;

          if (confirmationResponse.data.Success) {
            this.isTransactionSuccessful = true;
          } else {
            Notify.create({
              color: 'negative',
              position: 'top',
              message: confirmationResponse.data.Message,
            });
          }
        }
      });

      confirmationPromise.catch((error: AxiosError<ErrorResponseDTO>) => {
        if (error.status == 400) {
          this.router.push({
            name: 'PayByLinkError',
            params: {
              propertyCode: propertyStore.propertyCode,
            },
          });
        }
      });

      confirmationPromise.finally(() => {
        this.isLoadingConfirmation = false;
      });

      return confirmationPromise;
    },

    getPayByLinkConfig(propertyId: number) {
      this.propertyId = propertyId;
      const promise = apiService.payByLinkConfig(propertyId);

      promise.then((response) => {
        this.config = response.data;
      });

      return promise;
    },

    getPayByLinkInformation(
      actionType: string,
      invoiceNumber: string,
      code: string,
      id?: string,
    ) {
      const action: PayByLinkAction =
        actionType == 'P' ? PayByLinkAction.Pay : PayByLinkAction.StoreCard;

      const query = {
        propertyId: this.propertyId,
        invoiceNumber: invoiceNumber,
        hash: code,
        actionType: action,
        paymentId: id,
      };
      const promise = apiService.payByLinkGet(query);

      promise.then((response) => {
        if (response.data.Success) {
          this.Type = response.data.Type as PayByLinkType;
          this.Action = response.data.Action as PayByLinkAction;
          this.AmountToPay = response.data.AmountToPay;
          this.InvoiceNumber = response.data.InvoiceNumber;
          this.Description = response.data.Description;

          this.transaction = new PayByLinkTransaction(
            this.propertyId,
            response.data,
            this.config,
          );

          this.TransactionReference = response.data.TransactionReference;
          if (this.TransactionReference != null) {
            this.isTransactionSuccessful = true;
          }
        } else {
          this.invalidLinkMessage = response.data.Message;
        }
      });


      promise.catch((promiseAxiosError: AxiosError<ErrorResponseDTO | string>) => {
        const data = promiseAxiosError.response?.data;

        if (isErrorResponseDTO(data)) {
          this.invalidLinkMessage = data.Message || 'Something went wrong, failed to load Pay By Link';
        } else if (typeof data === 'string' && data.trim().length > 0) {
          this.invalidLinkMessage = data;
        } else {
          this.invalidLinkMessage = 'Something went wrong, failed to load Pay By Link';;
        }
      });

      return promise;
    },

    processTransaction() {
      this.isProcessing = true;
      const data = this.transaction.toDTO();
      const promise = apiService.payByLinkProcess(data);

      promise.catch(
        (
          promiseAxiosError: AxiosError<
            ErrorResponseDTO | string | PayByLinkConfirmationResponseDTO
          >,
        ) => {
          this.isProcessing = false;

          const obj = promiseAxiosError.response.data;
          let message = 'Failed to process transaction';

          if (typeof obj === 'string') {
            message = obj;
          } else if (
            isErrorResponseDTO(obj) ||
            isPayByLinkConfirmationResponseDTO(obj)
          ) {
            message = obj.Message;
          }

          Notify.create({
            color: 'negative',
            position: 'top',
            message: message,
          });
        },
      );

      return promise;
    },
  },
});
